import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Trophy, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePremium } from '@/hooks/usePremium';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { STRIPE_CONFIG } from '@/lib/stripeConfig';
import { Logo } from '@/components/Logo';

const PRO_FEATURES = [
  '🏈 All sports unlocked',
  '🎓 Advanced & expert lessons',
  '📊 Multi-sport progress dashboard',
  '📥 Downloadable playbooks & cheat sheets',
  '🏅 Certificates of completion',
  '⚡ Priority access to new sports',
];

export function UpgradeModal() {
  const {
    showUpgrade,
    setShowUpgrade,
    upgradeSportName,
    foundingMembersRemaining,
    startCheckout,
  } = usePremium();
  const { isPlayer } = usePlayerProfiles();

  const showFoundingOffer = foundingMembersRemaining > 0;

  const handleCheckout = async (plan: 'monthly' | 'annual' | 'founding') => {
    await startCheckout(STRIPE_CONFIG[plan].price_id);
  };

  // Child player accounts cannot purchase or change plans.
  if (isPlayer) return null;

  return (
    <AnimatePresence>
      {showUpgrade && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-foreground/50 backdrop-blur-sm p-4"
          onClick={() => setShowUpgrade(false)}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="bg-card rounded-2xl border border-border shadow-elevated w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Founding Member Banner */}
            {showFoundingOffer && (
              <div className="bg-accent text-accent-foreground px-4 py-2.5 text-center">
                <p className="font-display font-bold text-sm">
                  🎉 Founding Member Offer — Lock in Pro for $3.99/month forever
                </p>
                <p className="text-xs opacity-80 mt-0.5">
                  Only available to our first 200 members · <span className="font-bold">{foundingMembersRemaining} spots remaining</span>
                </p>
              </div>
            )}

            {/* Header */}
            <div className="bg-secondary text-secondary-foreground p-6 text-center relative">
              <button
                onClick={() => setShowUpgrade(false)}
                className="absolute top-3 right-3 text-secondary-foreground/50 hover:text-secondary-foreground"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Trophy className="w-6 h-6 text-accent" />
                <h2 className="font-display font-bold text-2xl">You're on a roll! 🏆</h2>
              </div>
              <p className="font-display font-semibold text-base text-secondary-foreground/80">
                Unlock every sport with Sportara Pro
              </p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {upgradeSportName
                  ? `You've started your journey with ${upgradeSportName}. Ready to explore more? Sportara Pro gives you full access to every sport, advanced lessons, and exclusive playbooks.`
                  : `You're making great progress! Sportara Pro gives you full access to every sport, advanced lessons, and exclusive playbooks.`
                }
              </p>

              {/* Features list */}
              <div className="space-y-2">
                {PRO_FEATURES.map((feature, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-2"
                  >
                    <span className="text-sm">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className="px-6 pb-6 space-y-3">
              {/* Founding Member CTA */}
              {showFoundingOffer && (
                <Button
                  onClick={() => handleCheckout('founding')}
                  className="w-full h-14 text-base font-display font-bold bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl"
                >
                  <Sparkles className="mr-2 w-5 h-5" />
                  Lock in $3.99/month forever
                </Button>
              )}

              {/* Monthly CTA */}
              <Button
                onClick={() => handleCheckout('monthly')}
                className="w-full h-14 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shadow-glow-primary"
              >
                <Crown className="mr-2 w-5 h-5" />
                Upgrade to Pro — $6.99/month
              </Button>

              {/* Annual CTA */}
              <Button
                onClick={() => handleCheckout('annual')}
                variant="outline"
                className="w-full h-12 text-sm font-display font-bold rounded-xl border-2"
              >
                See annual plan ($49.99/year — save 40%)
              </Button>

              <button
                onClick={() => setShowUpgrade(false)}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors font-body pt-1"
              >
                No thanks, stick with one sport for now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
